import { useMutation } from '@apollo/client'
import type { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { notification } from 'antd'
import { AddNuggetToProjectMutation } from 'api/graphQL/mutations/projectElement.mutations'
import {
    useActionBarActions,
    useElementList,
} from 'components/WorkspaceEditor/ActionBarStore'
import { useElementItemStore } from 'components/WorkspaceEditor/ElementItemStore'
import { productOptions } from 'pages/components/ProductListingDialog/ProductListingDialog.old'
import { useCallback } from 'react'
import { useWorkspace2Store } from '../../../Workspace2Store'
import { PT_ERROR_MESSAGES, PT_LABELS } from '../constants'
import { usePTActivationStore } from '../store'
import {
    deduplicateNodesByBaseElementId,
    preparePTMutationInputs,
} from '../utils'
import { useFamilyGroupSync } from './useFamilyGroupSync'

/**
 * Internal hook: Handles PT addition to project (single or multiple PTs)
 */
export const usePTAddition = () => {
    const projectId = useWorkspace2Store((state) => state.projectId)
    const [addProductTypeToProject] = useMutation(AddNuggetToProjectMutation)
    const elementList = useElementList()
    const { setElementList } = useActionBarActions()
    const selectedElementFormat = useElementItemStore(
        (state) => state.selectedElementFormat
    )
    const selectedElementFilterValue = useElementItemStore(
        (state) => state.selectedElementFilterValue
    )
    const setUpdatedElementNodes = usePTActivationStore(
        (state) => state.setUpdatedElementNodes
    )
    const openProgressDialog = usePTActivationStore(
        (state) => state.openProgressDialog
    )
    const closeProgressDialog = usePTActivationStore(
        (state) => state.closeProgressDialog
    )
    const setFamilyAddedProductTypes = useWorkspace2Store(
        (state) => state.actions.setFamilyAddedProductTypes
    )
    const { syncFamilyGroupNodes } = useFamilyGroupSync()

    const addPTsToProject = useCallback(
        async (ptElementNodes: ProjectElementNode[]) => {
            if (ptElementNodes.length === 0) {
                return
            }

            try {
                // Format PT count for progress message
                const ptCount = ptElementNodes.length
                const progressMessage = PT_LABELS.COUNT_WITH_LABEL(ptCount)

                // Open single progress dialog for all PTs
                openProgressDialog(progressMessage)

                // Add all PTs sequentially
                const addPTMutationResults = []
                for (const ptNode of ptElementNodes) {
                    const mutationInput = preparePTMutationInputs(
                        ptNode,
                        projectId,
                        selectedElementFormat
                    )

                    const addPTMutationResult = await addProductTypeToProject({
                        variables: { input: mutationInput },
                    })

                    addPTMutationResults.push(addPTMutationResult)
                }

                // Collect all updated nodes from mutation responses
                const allUpdatedNodes = addPTMutationResults.flatMap(
                    (addPTMutationResult) =>
                        addPTMutationResult?.data?.addNuggetToProject
                            ?.projectElementNodes || []
                )

                // CRITICAL: Deduplicate nodes by baseElementId, preferring nodes with isInProject: true
                // Each mutation returns the FULL element tree (41 nodes), so we have duplicates
                // We need to keep the version where isInProject: true for each PT
                const deduplicatedNodes = deduplicateNodesByBaseElementId(
                    allUpdatedNodes
                )

                // Update familyAddedProductTypes to prevent stale state when viewing a family
                // (fixes: isFamilySelected path in resolveProductTypeIds returning PT_NOT_FOUND)
                const activatedPTBaseIds = new Set(
                    ptElementNodes.map((pt) => pt.baseElementId).filter(Boolean)
                )
                const newFamilyPTNodes = deduplicatedNodes.filter(
                    (n) =>
                        n.elementType === 'PRODUCTTYPE' &&
                        n.baseElementId &&
                        activatedPTBaseIds.has(n.baseElementId)
                )
                if (newFamilyPTNodes.length > 0 && setFamilyAddedProductTypes) {
                    const currentFamilyPTs =
                        useWorkspace2Store.getState().familyAddedProductTypes ||
                        []
                    setFamilyAddedProductTypes([
                        ...currentFamilyPTs,
                        ...newFamilyPTNodes,
                    ])
                }

                // If "Added To Project" filter is ON, only include nodes with isInProject: true
                // This ensures usePTElementNodesUpdate only processes nodes that should be in the filtered list
                const isAddedToProjectFilterActive =
                    selectedElementFilterValue ===
                    productOptions.ADDED_TO_PROJECT

                const nodesToUpdate = isAddedToProjectFilterActive
                    ? deduplicatedNodes.filter(
                          (node) => node.isInProject === true
                      )
                    : deduplicatedNodes

                if (nodesToUpdate.length > 0) {
                    setUpdatedElementNodes(nodesToUpdate)
                }

                await syncFamilyGroupNodes(ptElementNodes)

                return addPTMutationResults
            } catch (error) {
                closeProgressDialog()
                const ptCount = ptElementNodes.length
                notification.error({
                    message: PT_ERROR_MESSAGES.ADD_TO_PROJECT_FAILED(ptCount),
                    description:
                        error instanceof Error
                            ? error.message
                            : PT_ERROR_MESSAGES.UNEXPECTED_ERROR(ptCount),
                    duration: 5,
                    placement: 'topRight',
                })

                throw error
            }
        },
        [
            addProductTypeToProject,
            projectId,
            selectedElementFormat,
            selectedElementFilterValue,
            setUpdatedElementNodes,
            openProgressDialog,
            closeProgressDialog,
            setFamilyAddedProductTypes,
            syncFamilyGroupNodes,
            elementList,
            setElementList,
        ]
    )

    return { addPTsToProject }
}
